/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - React Router</h2>
    <p className={styles.description}>
      Предоставляет набор компонентов для управления частями URL-строки и отображения различных компонетов в зависимости
      от текущего ее состояния. Разбит на пакеты для различных платформ, нас интересует{' '}
      <code className={styles.code}>react-router-dom</code>.
    </p>
    <CopyBlock text={`npm install react-router-dom`} language="javascript" showLineNumbers={false} theme={ocean} />
    <p className={styles.description}>
      В React Router есть три типа компонентов: компонент маршрутизатора, компоненты согласования маршрутов и компоненты
      навигации.{' '}
    </p>
    <a
      className={styles.link}
      href="https://reacttraining.com/react-router/web/guides/philosophy"
      target="_blank"
      rel="noopener noreferrer"
    >
      Документация React Router
    </a>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - BrowserRouter</h3>
      <p className={styles.description}>
        В основе каждого одностраничного приложения стоит маршрутизатор. Компонент{' '}
        <code className={styles.code}>{`<BrowserRouter>`}</code> создает раутер и объект{' '}
        <code className={styles.code}>history</code>, чтобы синхронизировать интерфейс с URL-адресом. Используя
        контекст, передает данные о текущем URL всему поддереву компонентов.{' '}
      </p>
      <CopyBlock
        text={`import { BrowserRouter } from 'react-router-dom';

ReactDOM.render(
  <BrowserRouter>
    <App />
  </BrowserRouter>,
  document.querySelector('#root'),
);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a
          className={styles.link}
          href="https://reacttraining.com/react-router/web/api/BrowserRouter"
          target="_blank"
          rel="noopener noreferrer"
        >
          Документация BrowserRouter
        </a>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Route</h3>
      <p className={styles.description}>
        Компонент позволяющий связать определенный URL и компонент для рендера. Его задача заключается в том, чтобы
        отобразить некоторый интерфес, когда <code className={styles.code}>location.pathname</code> соответствует
        значению пропа <code className={styles.code}>path</code>. Компонент{' '}
        <code className={styles.code}>{`<Route>`}</code> можно использовать в любом месте где необходимо рендерить
        контент на основе текущего URL.{' '}
      </p>
      <CopyBlock
        text={`import React from 'react';
import { Route } from 'react-router-dom';

const App = () => (
  <>
    <Route path="/" exact component={Home} />
    <Route path="/about" component={About} />
    <Route path="/contact" component={Contact} />
  </>
);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.notificationInfo}>
        Согласование маршрута выполняется путем сопоставления пропа <code className={styles.code}>path</code> и текущего
        значения <code className={styles.code}>location.pathname</code>. Если значение{' '}
        <code className={styles.code}>location.pathname</code> начинается на указанный путь в{' '}
        <code className={styles.code}>path</code>, <code className={styles.code}>{`<Route>`}</code> отрендерит указанный
        компонент, в противном случае вернет <code className={styles.code}>null</code>.{' '}
      </p>
      <p className={styles.description}>
        Когда <code className={styles.code}>location.pathname</code> начинается на{' '}
        <code className={styles.code}>'/about'</code>, первый и третий рауты отрендерят{' '}
        <code className={styles.code}>null</code>, а второй отрендерит компонент{' '}
        <code className={styles.code}>{`<About>`}</code>.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Проп <code className={styles.code}>exact</code> указывает на необходимость точного совпадения{' '}
          <code className={styles.code}>path</code> и <code className={styles.code}>location.pathname</code>
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>{`<Route>`}</code> без указанного <code className={styles.code}>path</code>{' '}
          всегда рендерит компонент
        </li>
      </ul>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.2.1 - Проп component</h4>
        <p className={styles.description}>
          Используется когда компонент необходимо отрендерить без передачи дополнительных пропсов. Стандартные пропсы{' '}
          <code className={styles.code}>match</code>, <code className={styles.code}>location</code> и{' '}
          <code className={styles.code}>history</code> будут переданы компоненту автоматически.{' '}
        </p>
        <p className={styles.description}>
          Для создания компонента используется <code className={styles.code}>React.createElement()</code>, это означает,
          что если вместо ссылки на компонент будет передана анонимная функция, для каждого ре-рендера будет создан
          новый компонент. Это приведет к размонтированию существующего компонента, созданию и монтированию нового на
          каждом рендере, вместо обновления существующего компонента.{' '}
        </p>
        <CopyBlock
          text={`// ✅ Хорошо
<Route path="/about" component={About} />

// ❌ Плохо
<Route
  path="/about"
  component={props => <About {...props} extraProp="amazing prop" />}
/>`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.2.2 - Проп render</h4>
        <p className={styles.description}>
          Позволяет использовать инлайн-функцию вместо компонента без нежелательного эффекта ре-рендера как в случае с{' '}
          <code className={styles.code}>component</code>. Используется тогда, когда компоненту необходимо передать
          дополнительные пропсы, кроме тех что передает <code className={styles.code}>{`<Route>`}</code>
        </p>
        <CopyBlock
          text={`<Route
  path="/about"
  render={props => <About {...props} extraPropName="value" />}
/>`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          <a
            className={styles.link}
            href="https://reacttraining.com/react-router/web/api/Route"
            target="_blank"
            rel="noopener noreferrer"
          >
            Документация Route
          </a>
        </p>
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.3 - Switch</h3>
      <p className={styles.description}>
        Группирует и отображает первый дочерний маршрут, <code className={styles.code}>path</code> которого
        соответствует текущему <code className={styles.code}>location.pathname</code>, игнорруя все последующие.{' '}
      </p>
      <CopyBlock
        text={`import React from 'react';
import { Switch, Route } from 'react-router-dom';

const App = () => (
  <div>
    <Switch>
      <Route path="/" exact component={Home} />
      <Route path="/about" component={About} />
      <Route path="/contact" component={Contact} />
      <Route component={NotFound} />
    </Switch>
  </div>
);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.4 - Redirect</h3>
      <p className={styles.description}>
        Позволяет декларативно отрендерить компонент который во время маунта перенаправит пользователя по указанному
        маршруту. Под капотом использует императивный интерфейс <code className={styles.code}>history</code>.{' '}
      </p>
      <CopyBlock
        text={`import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

const App = () => (
  <div>
    <Switch>
      <Route exact path="/" component={Home} />
      <Route path="/about" component={About} />
      <Route path="/contact" component={Contact} />
      <Redirect to="/" />
    </Switch>
  </div>
);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          По умолчанию подменяет текущую запись в истории, для того чтобы добавить новую запись на верх стека можно
          передать проп <code className={styles.code}>push</code>.
        </li>
        <li className={styles.list__item}>
          Вместо строки, проп <code className={styles.code}>to</code> может принимать полноценный объект формата{' '}
          <code className={styles.code}>location</code>.
        </li>
      </ul>
      <p className={styles.description}>
        <a
          className={styles.link}
          href="https://reacttraining.com/react-router/web/api/Redirect"
          target="_blank"
          rel="noopener noreferrer"
        >
          Документация Redirect
        </a>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.5 - Link и NavLink</h3>
      <p className={styles.description}>
        Для создания навигации нельзя использовать обычный HTML-тег{' '}
        <code className={styles.code}>{`<a href="/about">`}</code>. При клике, вместо того чтобы изменить URL на текущей
        странице, и позволить раутеру выполнить навигацию на стороне клиента, браузер выполнит GET-запрос и обновит
        страницу, а это не то, что нам нужно.{' '}
      </p>
      <p className={styles.description}>
        Компоненты <code className={styles.code}>{`<Link>`}</code> и <code className={styles.code}>{`<NavLink>`}</code>{' '}
        используются для создания ссылок. Они рендерят HTML-тег <code className={styles.code}>{`<a>`}</code>, но с
        расширенным функционалом клика, используя возможности объекта <code className={styles.code}>history</code> по
        замене URL.{' '}
      </p>
      <CopyBlock
        text={`<Link to="/books">Books</Link>

<Link to="/books?category=adventure#treasure-island">Adventure books</Link>`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Проп <code className={styles.code}>to</code> можно передавать в виде строки описывающей{' '}
        <code className={styles.code}>href</code> будущей ссылки, или как объект{' '}
        <code className={styles.code}>location</code> со следующими (необязательными) свойствами:{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>pathname</code> - строка, путь для ссылки.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>search</code> - строковое представление параметров запроса.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>hash</code> - хэш для добавления в конец URL.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>state</code> - объект, который будет записан в{' '}
          <code className={styles.code}>location.state</code> после перехода по ссылке.
        </li>
      </ul>
      <CopyBlock
        text={`<Link
  to={{
    pathname: '/books',
    search: '?category=adventure',
    hash: '#treasure-island',
    state: { from: '/dashboard' },
  }}
/>`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Компонент <code className={styles.code}>{`<NavLink>`}</code> отличается от{' '}
        <code className={styles.code}>{`<Link>`}</code> только тем, что может иметь дополнительные стили, если текущий
        URL совпадает со значением пропа <code className={styles.code}>to</code>.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>activeClassName</code> - строка классов для объеденения с{' '}
          <code className={styles.code}>className</code> когда элемент активен.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>activeStyle</code> - объект инлайн стилей для добавления к элементу когда он
          активен.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>exact</code> - когда <code className={styles.code}>true</code>, активные
          классы/стили будут применяться только в том случае, если местоположение точно совпадает со значением пропа{' '}
          <code className={styles.code}>to</code>.
        </li>
      </ul>
      <CopyBlock
        text={`<NavLink to="/books" className="link" activeClassName="active-link">
  Books
</NavLink>`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://reacttraining.com/react-router/web/api/Link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Документация Link
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://reacttraining.com/react-router/web/api/NavLink"
            target="_blank"
            rel="noopener noreferrer"
          >
            Документация NavLink
          </a>
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.6 - Route props</h3>
      <p className={styles.description}>
        Компоненту отрендеренному через <code className={styles.code}>{`<Route>`}</code> будет передано несколько
        специальных пропсов хранящих много полезной информации.
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>match</code> — объект с информацией о том как совпали{' '}
          <code className={styles.code}>path</code> и <code className={styles.code}>location.pathname</code>
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>location</code> - объект хранящий информацию о текущем URL
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>history</code> - объект истории, созданный самим раутером
        </li>
      </ul>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.6.1 - Проп match</h4>
        <p className={styles.description}>
          Объект, хранит информацию о том, как <code className={styles.code}>path</code> совпал с{' '}
          <code className={styles.code}>location.pathname</code>. Содержит следующие свойства.{' '}
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>params</code> - объект пар <code className={styles.code}>ключ:значение</code>,
            соответствующих динамическим параметрам URL.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>isExact</code> - указыват на точное соотвествие{' '}
            <code className={styles.code}>path</code> и <code className={styles.code}>location.pathname</code>.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>path</code> - паттерн пути на который замачился{' '}
            <code className={styles.code}>{`<Route>`}</code>. Используется для создания вложенных маршрутов.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>url</code> - совпавшая часть URL-адреса. Используется для создания вложенной
            навигации.
          </li>
        </ul>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.6.2 - Проп location</h4>
        <p className={styles.description}>
          Объект, свойства которого описывают текущее местоположение, путь куда будет произведен переход или откуда
          пришли на текущий маршрут. Можно использовать в том числе для проверки в{' '}
          <code className={styles.code}>componentDidUpdate</code> изменился ли текущий URL.{' '}
        </p>
        <CopyBlock
          text={`{
  key: 'ac3df4',
  pathname: '/books',
  search: '?sortby=latest',
  hash: '#comments',
  state: {
    from: '/login'
  }
}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.6.3 - Проп history</h4>
        <p className={styles.description}>
          Объект истории со свойствами и методами для программной навигации. Используется для перенаправлений.{' '}
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>history.push(path [, state])</code> - добавляет новую запись на стек записей
            истории.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>history.replace(path [, state])</code> - подменяет текущую запись на новую на
            стеке записей истории.
          </li>
        </ul>
        <a
          className={styles.link}
          href="https://reacttraining.com/react-router/web/api/Route/route-props"
          target="_blank"
          rel="noopener noreferrer"
        >
          Документация Route props
        </a>
      </section>
    </section>
  </section>
))
